// TaskSourceLink — the ONE renderer for a task's "go to source" affordance
// (SEM-5799 / M5). Both surfaces that show it (tasks-table row actions, the
// task-detail drawer) go through here so the external-vs-internal distinction
// can never drift between them.
//
// Three renderings, one per classifyTaskLink verdict:
//   internal → a plain in-app link, unchanged from before.
//   external → NEW tab + rel=noopener/noreferrer (Link's `external` prop), an
//              external-link glyph, and the destination HOST rendered in the
//              label. An unmarked off-site destination wearing in-product chrome
//              is exactly the lever the M5 finding describes.
//   unsafe   → NOT an anchor at all. A row written before the server-side
//              allowlist may carry `javascript:`/`data:`; we show that the link
//              was withheld rather than handing the reader a live href.

import { forwardRef } from "react";
import { useTranslation } from "react-i18next";
import { ExternalLink as ExternalLinkIcon } from "lucide-react";

import { Link } from "@novemai/components";

import { classifyTaskLink, taskLinkHost } from "./task-link";

export interface TaskSourceLinkProps
  extends Omit<React.HTMLAttributes<HTMLElement>, "children"> {
  readonly link: string;
}

/** forwardRef + prop spread: the detail drawer renders this inside
 *  `<Button asChild>`, which clones the child with its own props and ref. */
export const TaskSourceLink = forwardRef<HTMLElement, TaskSourceLinkProps>(
  ({ link, className, ...rest }, ref) => {
    const { t } = useTranslation();
    const kind = classifyTaskLink(link);

    if (kind === "unsafe") {
      return (
        <span
          ref={ref as React.Ref<HTMLSpanElement>}
          className={className}
          data-task-link="unsafe"
          title={t("tasks.link_blocked_hint")}
          {...rest}
        >
          {t("tasks.link_blocked")}
        </span>
      );
    }

    if (kind === "external") {
      return (
        <Link
          ref={ref as React.Ref<HTMLAnchorElement>}
          href={link}
          external
          underline="hover"
          className={className}
          data-task-link="external"
          title={t("tasks.go_to_source_external_hint")}
          {...rest}
        >
          {t("tasks.go_to_source_external", { host: taskLinkHost(link) })}
          <ExternalLinkIcon aria-hidden="true" className="ml-1 inline size-3.5 align-[-2px]" />
        </Link>
      );
    }

    return (
      <Link
        ref={ref as React.Ref<HTMLAnchorElement>}
        href={link}
        underline="hover"
        className={className}
        data-task-link="internal"
        {...rest}
      >
        {t("tasks.go_to_source")}
      </Link>
    );
  },
);

TaskSourceLink.displayName = "TaskSourceLink";
